import { Employee, Attendance, Break, KPIData, Shift, AppSettings } from '../types';
import { calculatePayroll } from './payrollUtils';

/**
 * Rounds a date to the nearest X minutes.
 * @param date The date to round
 * @param minutes The nearest minute interval (e.g. 15)
 */
export const getRoundedTime = (date: Date, minutes: number): Date => {
    if (minutes === 0) return date;
    const ms = 1000 * 60 * minutes;
    return new Date(Math.round(date.getTime() / ms) * ms);
};

export const calculatePayableDuration = (
    start: Date,
    end: Date,
    breakDurationMs: number,
    settings: AppSettings,
    scheduledShift?: Shift
): number => {
    let s = start;
    let e = end;

    // Snap-to-Shift Logic
    // If enabled, and clock-in is early relative to shift start, move start time forward to shift start.
    if (settings.snapToShift && scheduledShift) {
        const shiftStart = new Date(scheduledShift.start);
        if (s < shiftStart) {
            s = shiftStart;
        }
    }

    const rounding = settings.attendanceRounding || 0;

    if (rounding > 0) {
        s = getRoundedTime(s, rounding);
        e = getRoundedTime(e, rounding);
    }

    let duration = e.getTime() - s.getTime() - breakDurationMs;

    if (settings.autoDeductEnabled) {
        const thresholdMs = (settings.autoDeductThreshold || 0) * 60 * 60 * 1000;
        const deductionMs = (settings.autoDeductAmount || 0) * 60 * 1000;

        if (duration > thresholdMs) {
            duration -= deductionMs;
        }
    }

    return Math.max(0, duration);
};

export const calculateKPIData = (employees: Employee[], attendance: Attendance[], breaks: Break[], shifts: Shift[], settings?: AppSettings): KPIData => {
    try {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        today.setHours(0, 0, 0, 0);

        // Calculate start of the week (Monday)
        const dayOfWeek = now.getDay(); // Sunday - 0, Monday - 1, ...
        const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust when day is Sunday
        const startOfWeek = new Date(new Date(now).setDate(diff));
        startOfWeek.setHours(0, 0, 0, 0);

        const totalEmployees = employees.length;
        const clockedIn = employees.filter(e => e.status === 'Clocked In' || e.status === 'On Break').length;
        const activeBreaks = employees.filter(e => e.status === 'On Break').length;

        // -- PRE-CALCULATION MAPS (Optimizes performance from O(N*M) to O(N+M)) --
        const shiftMapByDay = new Map<string, Shift>(); // key: employeeId-toDateString
        shifts.forEach(s => {
            if (s && s.start && s.type === 'work' && s.status !== 'pending') {
                const dateStr = new Date(s.start).toDateString();
                shiftMapByDay.set(`${s.employeeId}-${dateStr}`, s);
            }
        });

        const todaysAttendance = attendance.filter(a => a.clockIn && new Date(a.clockIn) >= today);
        const thisWeeksAttendance = attendance.filter(a => a.clockIn && new Date(a.clockIn) >= startOfWeek);

        // 1. LIVE BURN & TODAY'S TOTALS
        let liveLaborBurnRate = 0;
        employees.forEach(emp => {
            if (emp.status === 'Clocked In' || emp.status === 'On Break') {
                liveLaborBurnRate += (emp.hourlyRate || 0);
            }
        });

        let totalCostAccruedToday = 0;
        let totalHoursToday = 0;
        let lateArrivals = 0;

        todaysAttendance.forEach(att => {
            if (!att.clockIn) return;
            const clockInTime = new Date(att.clockIn).getTime();
            const clockOutTime = att.clockOut ? new Date(att.clockOut).getTime() : now.getTime();
            
            if (isNaN(clockInTime) || isNaN(clockOutTime)) return;

            const durationMs = Math.max(0, clockOutTime - clockInTime);
            const durationHrs = durationMs / (1000 * 60 * 60);

            const emp = employees.find(e => e.id === att.employeeId);
            if (emp) {
                totalCostAccruedToday += (durationHrs * (emp.hourlyRate || 0));
                totalHoursToday += durationHrs;

                // Late Arrival Check (only for work shifts)
                const dateStr = new Date(att.clockIn).toDateString();
                const scheduledShift = shiftMapByDay.get(`${emp.id}-${dateStr}`);
                if (scheduledShift && scheduledShift.type === 'work') {
                    const shiftStart = new Date(scheduledShift.start);
                    const graceMinutes = settings?.gracePeriod !== undefined ? settings.gracePeriod : 5;
                    if (new Date(att.clockIn).getTime() > (shiftStart.getTime() + (graceMinutes * 60 * 1000))) {
                        lateArrivals++;
                    }
                }
            }
        });

        // 2. WEEKLY TOTALS
        let totalCostAccruedThisWeek = 0;
        let estimatedPayrollThisWeek = 0;
        let totalHoursThisWeek = 0;

        const dailyHoursMap = new Map<string, number>(); // key: employeeId-YYYY-MM-DD

        thisWeeksAttendance.forEach(att => {
            if (!att.clockIn) return;
            const clockInDate = new Date(att.clockIn);
            const clockInTime = clockInDate.getTime();
            const clockOutTime = att.clockOut ? new Date(att.clockOut).getTime() : (new Date(att.clockIn).toDateString() === now.toDateString() ? now.getTime() : new Date(att.clockIn).setHours(23,59,59,999));
            
            if (isNaN(clockInTime) || isNaN(clockOutTime)) return;

            const durationMs = Math.max(0, clockOutTime - clockInTime);
            const durationHrs = durationMs / (1000 * 60 * 60);
            
            const emp = employees.find(e => e.id === att.employeeId);
            if (emp) {
                totalCostAccruedThisWeek += (durationHrs * (emp.hourlyRate || 0));
                totalHoursThisWeek += durationHrs;

                // Track daily totals for accurate payroll calculation
                const dayKey = `${emp.id}-${clockInDate.toISOString().split('T')[0]}`;
                dailyHoursMap.set(dayKey, (dailyHoursMap.get(dayKey) || 0) + durationHrs);
            }
        });

        // 3. PRE-CALCULATE SICK DAYS FOR THIS WEEK
        const sickDaysMap = new Map<string, number>(); // key: employeeId
        shifts.forEach(s => {
            if (s.type === 'sick' && s.status !== 'pending') {
                const shiftDate = new Date(s.start);
                if (shiftDate >= startOfWeek && shiftDate <= now) {
                    sickDaysMap.set(s.employeeId, (sickDaysMap.get(s.employeeId) || 0) + 1);
                }
            }
        });

        // 4. PAYROLL ESTIMATE (Weekly)
        const weeklyEmployeeTotals = new Map<string, { hours: number, emp: Employee, dates: string[] }>();
        dailyHoursMap.forEach((hours, key) => {
            const [empId, date] = key.split('-');
            if (!weeklyEmployeeTotals.has(empId)) {
                const emp = employees.find(e => e.id === empId);
                if (emp) weeklyEmployeeTotals.set(empId, { hours: 0, emp, dates: [] });
            }
            const data = weeklyEmployeeTotals.get(empId);
            if (data) {
                data.hours += hours;
                data.dates.push(date);
            }
        });

        weeklyEmployeeTotals.forEach(({ hours, emp, dates }) => {
            const grossPay = hours * (emp.hourlyRate || 0);
            const lastDate = new Date(dates[dates.length - 1] || now);
            
            // Get pre-calculated Sick Days
            const sickDays = sickDaysMap.get(emp.id) || 0;
            const use2026Rules = settings?.ssp2026Rules || false;
            const sspDailyRate = settings?.sspDailyRate || 24.65;

            try {
                const { employerNi, sspAmount } = calculatePayroll(
                    grossPay, 
                    emp.taxCode || '1257L', 
                    'weekly', 
                    undefined, 
                    lastDate,
                    sickDays,
                    sspDailyRate,
                    use2026Rules
                );
                estimatedPayrollThisWeek += grossPay + (employerNi || 0) + (sspAmount || 0);
            } catch (pE) {
                console.error("Payroll calc error:", pE);
                estimatedPayrollThisWeek += grossPay;
            }
        });

        // Calculate missed clock outs
        const missedClockOuts = attendance.filter(a => {
            if (a.status !== 'active' || !a.clockIn) return false;
            const hoursSinceClockIn = (now.getTime() - new Date(a.clockIn).getTime()) / (1000 * 60 * 60);
            return hoursSinceClockIn > 12;
        }).length;

        // Calculate missed clock ins
        const todaysStartedWorkShifts = shifts.filter(s => {
            if (!s.start || s.type !== 'work' || s.status === 'pending') return false;
            const shiftStart = new Date(s.start);
            return shiftStart.toDateString() === now.toDateString() && shiftStart < now;
        });
        const missedClockIns = todaysStartedWorkShifts.filter(shift => 
            !attendance.some(att => att.employeeId === shift.employeeId && att.clockIn && new Date(att.clockIn).toDateString() === new Date(shift.start).toDateString())
        ).length;

        // Calculate total scheduled hours today
        let totalScheduledHoursToday = 0;
        shifts.forEach(s => {
            if (s.type === 'work' && s.status !== 'pending' && s.start && s.end) {
                const shiftStart = new Date(s.start);
                if (shiftStart.toDateString() === now.toDateString()) {
                    const durationHrs = (new Date(s.end).getTime() - shiftStart.getTime()) / (1000 * 60 * 60);
                    if (durationHrs > 0) totalScheduledHoursToday += durationHrs;
                }
            }
        });

        // Calculate total overtime this week
        let totalOvertimeThisWeek = 0;
        dailyHoursMap.forEach((dailyTotal) => {
            const overtimeHours = Math.max(0, dailyTotal - 8);
            totalOvertimeThisWeek += overtimeHours;
        });

        // Calculate absenteeism rate
        const sickToday = employees.filter(e => (e.status as string) === 'Sick').length;
        const holidayToday = employees.filter(e => (e.status as string) === 'Holiday').length;
        const absenteeismRate = totalEmployees > 0 ? ((sickToday + holidayToday) / totalEmployees) * 100 : 0;

        // 4. TRENDS & STATS
        const attendanceRate = totalEmployees > 0 ? (clockedIn / totalEmployees) * 100 : 0;
        const punctuality = todaysAttendance.length > 0 ? ((todaysAttendance.length - lateArrivals) / todaysAttendance.length) * 100 : 100;

        const safe = (val: number) => isFinite(val) ? (Math.round(val * 100) / 100) : 0;

        return {
            totalEmployees: safe(totalEmployees),
            clockedIn: safe(clockedIn),
            activeBreaks: safe(activeBreaks),
            lateArrivals: safe(lateArrivals),
            totalHoursToday: safe(totalHoursToday),
            totalHoursThisWeek: safe(totalHoursThisWeek),
            laborCostToday: safe(totalCostAccruedToday),
            laborCostThisWeek: safe(totalCostAccruedThisWeek),
            estimatedPayrollThisWeek: safe(estimatedPayrollThisWeek),
            liveLaborBurnRate: safe(liveLaborBurnRate),
            totalCostAccruedToday: safe(totalCostAccruedToday),
            attendanceRate: safe(attendanceRate),
            punctuality: safe(punctuality),
            laborCostTrend: 0, 
            pendingRequests: shifts.filter(s => s.status === 'pending' && s.type === 'holiday').length,
            pendingSickRequests: shifts.filter(s => s.status === 'pending' && s.type === 'sick').length,
            
            // Required properties from KPIData
            totalScheduledHoursToday: safe(totalScheduledHoursToday),
            missedClockOuts: safe(missedClockOuts),
            missedClockIns: safe(missedClockIns),
            totalOvertimeThisWeek: safe(totalOvertimeThisWeek),
            totalLaborCost: safe(totalCostAccruedThisWeek),
            absenteeismRate: safe(absenteeismRate)
        };
    } catch (e) {
        console.error("CRITICAL: calculateKPIData failed:", e);
        return {
            totalEmployees: employees.length,
            clockedIn: 0,
            activeBreaks: 0,
            lateArrivals: 0,
            totalHoursToday: 0,
            totalHoursThisWeek: 0,
            laborCostToday: 0,
            laborCostThisWeek: 0,
            estimatedPayrollThisWeek: 0,
            liveLaborBurnRate: 0,
            totalCostAccruedToday: 0,
            attendanceRate: 0,
            punctuality: 0,
            laborCostTrend: 0,
            pendingRequests: 0,
            pendingSickRequests: 0,
            
            // Required properties from KPIData
            totalScheduledHoursToday: 0,
            missedClockOuts: 0,
            missedClockIns: 0,
            totalOvertimeThisWeek: 0,
            totalLaborCost: 0,
            absenteeismRate: 0
        };
    }
};

/**
 * Returns the initials of a name (e.g., "John Doe" -> "JD").
 */
export const getInitials = (name: string): string => {
  if (!name) return '??';
  const parts = name.split(' ');
  const first = parts[0]?.[0] || '';
  const last = parts[1]?.[0] || '';
  return (first + last).toUpperCase();
};
